import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ErrorHandler,
  OnInit,
  Component,
  inject,
  signal,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { RouterLink } from '@angular/router';
import { finalize } from 'rxjs/operators';

import { CONFIG } from '../../../config';
import { NotificationType } from 'src/app/shared/component/notification/notification-type.enum';
import { NotificationComponent } from 'src/app/shared/component/notification/notification.component';
import { SeasonResourceService } from 'src/app/shared/season-resource/season-resource.service';
import { ISeason } from 'src/app/shared/season-resource/season.interface';

@Component({
  selector: 'app-index',
  template: `<div class="container main">
      @if (isLoading()) {
        <mat-progress-spinner
          color="primary"
          mode="indeterminate"
        ></mat-progress-spinner>
      }
      @if (!isLoading() && season(); as currentSeason) {
        <mat-card appearance="outlined">
          <mat-card-header>
            <mat-card-title
              >Saison {{ currentSeason.name }}&nbsp;:
              pré-inscriptions</mat-card-title
            >
          </mat-card-header>
          <mat-card-content class="mb-2">
            <p>
              Pour les pré-inscriptions
              @if (
                currentSeason.presubscription_cancel_only_date &&
                currentSeason.presubscription_opening_date
              ) {
                reçues entre le
                {{
                  currentSeason.presubscription_opening_date | date: 'longDate'
                }}
                et le
                {{
                  currentSeason.presubscription_cancel_only_date
                    | date: 'longDate'
                }}
              }
              @if (
                currentSeason.presubscription_cancel_only_date &&
                !currentSeason.presubscription_opening_date
              ) {
                reçues avant le
                {{
                  currentSeason.presubscription_cancel_only_date
                    | date: 'longDate'
                }}
              }
              , les places seront attri­buées selon l'ordre sui­vant :
            </p>
            <ol class="list-decimal pl-6 mb-2">
              <li>les adhérent⋅e⋅s de la sai­son pré­cé­dente&nbsp;;</li>
              <li>
                les per­sonnes de la famille proche d'un⋅e adhérent⋅e (frère,
                sœur, parent, enfant)&nbsp;;
              </li>
              <li>les habitant⋅e⋅s d'Anse&nbsp;;</li>
              <li>les pré-inscriptions dans l'ordre de récep­tion.</li>
            </ol>

            @if (currentSeason.presubscription_cancel_only_date) {
              <p>
                Nous tra­vaille­rons sur l'at­tri­bu­tion des places à par­tir
                du
                <strong>{{
                  currentSeason.presubscription_cancel_only_date
                    | date: 'longDate'
                }}</strong
                >, aus­si toute pré-inscription reçue après cette date ne
                pour­ra pas être assu­rée d'être trai­tée selon l'ordre
                pré­ci­sé ci-dessus.
              </p>
            }

            @if (!currentSeason.is_open) {
              <p>
                Les pré-inscriptions pour {{ currentSeason.name }} commencent le
                <strong>{{
                  currentSeason.presubscription_opening_date | date: 'longDate'
                }}</strong>
                à
                <strong>{{
                  currentSeason.presubscription_opening_date | date: 'shortTime'
                }}</strong
                >.
              </p>
            }
          </mat-card-content>
          @if (currentSeason.is_open) {
            <mat-card-actions>
              <a
                mat-raised-button
                color="accent"
                [routerLink]="[
                  '/season',
                  currentSeason.id,
                  'create-presubscription',
                ]"
                >Commencer la pré-inscription pour {{ currentSeason.name }}</a
              >
            </mat-card-actions>
          }

          @if (!currentSeason.is_open) {
            <mat-card-actions>
              <a
                mat-raised-button
                disabled
                color="accent"
                [routerLink]="[
                  '/season',
                  currentSeason.id,
                  'create-presubscription',
                ]"
                >Ouverture le
                <strong>{{
                  currentSeason.presubscription_opening_date | date: 'longDate'
                }}</strong>
                à
                <strong>{{
                  currentSeason.presubscription_opening_date | date: 'shortTime'
                }}</strong></a
              >
            </mat-card-actions>
          }
        </mat-card>
      }
      @if (!isLoading() && !season() && !error()) {
        <mat-card appearance="outlined">
          <mat-card-header>
            <mat-card-title>{{ title }}</mat-card-title>
          </mat-card-header>
          <mat-card-content>
            <p>Les pré-inscriptions sont fermées pour le moment.</p>
          </mat-card-content>
        </mat-card>
      }
      @if (error()) {
        <mat-card appearance="outlined">
          <app-notification
            [type]="notificationType.Error"
            title="Une erreur est survenue lors du chargement"
          ></app-notification>
        </mat-card>
      }
    </div>
    <style>
      mat-progress-spinner {
        margin: 0 auto;
      }
    </style>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatButtonModule,
    RouterLink,
    NotificationComponent,
  ],
})
export class IndexComponent implements OnInit {
  private seasonResource = inject(SeasonResourceService);
  private readonly errorHandler = inject(ErrorHandler);

  public readonly season = signal<ISeason | undefined>(undefined);
  public readonly error = signal<string | undefined>(undefined);
  public readonly isLoading = signal(true);
  public readonly title: string = CONFIG.app.name;
  public readonly notificationType = NotificationType;

  public ngOnInit(): void {
    this.seasonResource
      .list({})
      .pipe(
        finalize(() => {
          this.isLoading.set(false);
        }),
      )
      .subscribe({
        next: (seasons: ISeason[]) => {
          this.season.set(selectRelevantSeason(seasons));
        },
        error: (error: Error) => {
          this.error.set(error.message);
          this.errorHandler.handleError(error);
        },
      });
  }
}

/**
 * Selects the season to display on the landing page:
 * - the currently open season if there is one;
 * - otherwise the upcoming season with the soonest opening date, so the page
 *   can announce when the next presubscriptions will open.
 *
 * Already closed seasons (no longer open, opening date in the past) are ignored.
 */
function selectRelevantSeason(seasons: ISeason[]): ISeason | undefined {
  const openSeason = (seasons ?? []).find((season) => season.is_open);
  if (openSeason) {
    return openSeason;
  }

  const now = Date.now();
  const upcomingSeasons = (seasons ?? [])
    .filter(
      (season): season is ISeason & { presubscription_opening_date: Date } =>
        season.presubscription_opening_date !== undefined &&
        season.presubscription_opening_date.getTime() > now,
    )
    .sort(
      (a, b) =>
        a.presubscription_opening_date.getTime() -
        b.presubscription_opening_date.getTime(),
    );

  return upcomingSeasons[0];
}
